import React from 'react'
import { api, newIdempotencyKey } from '../api/client'
import type { NotePhoto } from '../types/app'
import { photoErrorMessage } from './notePhotos'

// A photo as resizeNotePhoto hands it over, ready to upload.
export type PickedPhoto = { blob: Blob; width: number; height: number }

// QueuedPhoto is a photo that is not on the note yet. 'waiting': picked while
// the note is being written. 'queued': bound to a saved note, its turn still
// to come. 'uploading': its request is in flight. 'failed': it didn't upload.
// url is an object URL of its blob for the preview, let go when it leaves
// the queue. idempotencyKey is made when the photo is picked and sent with
// every attempt, so a Try again after an answer that never arrived replays
// the first upload instead of storing the photo twice.
export interface QueuedPhoto {
  key: string
  photo: PickedPhoto
  idempotencyKey: string
  url: string
  status: 'waiting' | 'queued' | 'uploading' | 'failed'
  error?: string
}

// A photo as Save note found it, so the save can still send it once the
// queue on screen has been cleared.
export type HeldPhoto = { key: string; photo: PickedPhoto; idempotencyKey: string }

export interface NotePhotoQueue {
  items: QueuedPhoto[]
  // What went wrong with a failed photo when it is more than a dropped
  // connection (no consent, the limit), for a line under the tiles.
  problem: string | null
  add: (photo: PickedPhoto) => void
  // Drops a photo; one bound to a note but not yet sent is skipped.
  remove: (key: string) => void
  // Empties the queue on screen, for when the note is cancelled or the sheet
  // shows another state. Photos already bound to a note still go up.
  clear: () => void
  // The photos waiting for Save note, taken when she presses it.
  waiting: () => HeldPhoto[]
  // Binds every waiting photo (and any held ones) to the note and uploads
  // them one at a time.
  upload: (clientId: string, noteId: string, onUploaded: (photo: NotePhoto) => void, held?: HeldPhoto[]) => void
  retry: (key: string, clientId: string, noteId: string, onUploaded: (photo: NotePhoto) => void) => void
}

const DROPPED = photoErrorMessage(null)

// useNotePhotoQueue holds an appointment note's photos until they are on the
// note. Uploads run one after another so the API sees them in the order she
// picked them, and a seventh is refused cleanly rather than racing.
//
// Once a photo is bound to a saved note its request always runs, whatever
// happens to the sheet (closed, another appointment, the status changed): it
// is hers and the note exists. What the generation decides is only whether
// the answer still updates the tiles; onUploaded runs while the sheet is
// mounted (the caller checks the note id). A failure nobody can see any more
// is reported through onUnseenFailure.
export function useNotePhotoQueue(firstName?: string, onUnseenFailure?: () => void): NotePhotoQueue {
  const [items, setItems] = React.useState<QueuedPhoto[]>([])
  // The queue as of now, for uploads that read it after an await.
  const current = React.useRef<QueuedPhoto[]>([])
  const generation = React.useRef(0)
  const mounted = React.useRef(true)
  const chain = React.useRef<Promise<void>>(Promise.resolve())
  const nextKey = React.useRef(0)
  // Photos she removed: a bound one is skipped when its turn comes.
  const removed = React.useRef(new Set<string>())
  const name = React.useRef(firstName)
  name.current = firstName
  const unseen = React.useRef(onUnseenFailure)
  unseen.current = onUnseenFailure

  const commit = React.useCallback((next: QueuedPhoto[]) => {
    current.current = next
    if (mounted.current) setItems(next)
  }, [])

  const patch = React.useCallback(
    (key: string, change: Partial<QueuedPhoto>) => commit(current.current.map((i) => (i.key === key ? { ...i, ...change } : i))),
    [commit],
  )

  const drop = React.useCallback(
    (key: string) => {
      const item = current.current.find((i) => i.key === key)
      if (!item) return
      URL.revokeObjectURL(item.url)
      commit(current.current.filter((i) => i.key !== key))
    },
    [commit],
  )

  const remove = React.useCallback(
    (key: string) => {
      removed.current.add(key)
      drop(key)
    },
    [drop],
  )

  const add = React.useCallback(
    (photo: PickedPhoto) => {
      const key = `q${++nextKey.current}`
      commit([...current.current, { key, photo, idempotencyKey: newIdempotencyKey(), url: URL.createObjectURL(photo.blob), status: 'waiting' }])
    },
    [commit],
  )

  const clear = React.useCallback(() => {
    generation.current++
    if (current.current.length === 0) return
    for (const i of current.current) URL.revokeObjectURL(i.url)
    commit([])
  }, [commit])

  const waiting = React.useCallback(
    () => current.current.filter((i) => i.status === 'waiting').map((i) => ({ key: i.key, photo: i.photo, idempotencyKey: i.idempotencyKey })),
    [],
  )

  // send binds one photo to the note now: the photo and note id are fixed
  // here, and the request runs in its turn even if the sheet has moved on.
  const send = React.useCallback(
    (held: HeldPhoto, clientId: string, noteId: string, onUploaded: (photo: NotePhoto) => void) => {
      const { key, photo, idempotencyKey } = held
      const gen = generation.current
      const shown = () => mounted.current && gen === generation.current
      patch(key, { status: 'queued', error: undefined })
      chain.current = chain.current
        .then(async () => {
          if (removed.current.has(key)) return
          if (shown()) patch(key, { status: 'uploading' })
          let saved: NotePhoto
          try {
            saved = await api.uploadNotePhoto(clientId, noteId, photo, idempotencyKey)
          } catch (e) {
            if (shown()) patch(key, { status: 'failed', error: photoErrorMessage(e, name.current) })
            else unseen.current?.()
            return
          }
          if (shown()) drop(key)
          if (mounted.current) onUploaded(saved)
        })
        // Nothing a link's callbacks throw may stop the photos after it: a
        // rejected chain would skip every later link. The photo itself is on
        // the note by then, or marked failed.
        .catch(() => {})
    },
    [patch, drop],
  )

  const upload = React.useCallback(
    (clientId: string, noteId: string, onUploaded: (photo: NotePhoto) => void, held: HeldPhoto[] = []) => {
      // Held photos first (picked before Save note), then any picked since;
      // a photo in both keeps its first place.
      const photos = new Map<string, HeldPhoto>()
      for (const h of held) photos.set(h.key, h)
      for (const i of current.current) {
        if (i.status === 'waiting' && !photos.has(i.key)) photos.set(i.key, { key: i.key, photo: i.photo, idempotencyKey: i.idempotencyKey })
      }
      for (const [key, h] of photos) {
        if (!removed.current.has(key)) send(h, clientId, noteId, onUploaded)
      }
    },
    [send],
  )

  const retry = React.useCallback(
    (key: string, clientId: string, noteId: string, onUploaded: (photo: NotePhoto) => void) => {
      const item = current.current.find((i) => i.key === key)
      if (item) send({ key, photo: item.photo, idempotencyKey: item.idempotencyKey }, clientId, noteId, onUploaded)
    },
    [send],
  )

  // Closing the sheet lets every preview go; uploads already bound carry on.
  React.useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      generation.current++
      for (const i of current.current) URL.revokeObjectURL(i.url)
    }
  }, [])

  const problem = items.find((i) => i.status === 'failed' && i.error && i.error !== DROPPED)?.error ?? null
  return React.useMemo(
    () => ({ items, problem, add, remove, clear, waiting, upload, retry }),
    [items, problem, add, remove, clear, waiting, upload, retry],
  )
}
